import { CreateTaskTagDto, TagType } from '@sib/api';
import { ContactOption } from '../../types/contact';

export const MESSAGE_TITLE_MAX_LENGTH = 240;
export const MESSAGE_BODY_MAX_LENGTH = 1500;

export enum OrganizationEmailFormField {
  Recipient = 'recipient',
  Title = 'emailTitle',
  Message = 'message',
  ThreadMembers = 'threadMembers',
  ShouldCreateTask = 'shouldCreateTask',
}

export interface EmailContactsValue {
  readonly [TagType.Contact]: string[];
  readonly [TagType.Employee]: string[];
}

export interface OrganizationEmailFormValues {
  [OrganizationEmailFormField.Recipient]: EmailContactsValue;
  [OrganizationEmailFormField.Title]: string;
  [OrganizationEmailFormField.Message]: string;
  [OrganizationEmailFormField.ThreadMembers]: EmailContactsValue;
  [OrganizationEmailFormField.ShouldCreateTask]: boolean;
}

interface OrganizationEmailDefaultValuesOptions {
  shouldCreateTask?: boolean;
}

const getEmailContactsValue = (
  value?: Partial<EmailContactsValue>
): EmailContactsValue => ({
  [TagType.Contact]: value?.[TagType.Contact] ?? [],
  [TagType.Employee]: value?.[TagType.Employee] ?? [],
});

/**
 * Builds the email form default values, restoring any stored draft values.
 */
export const getOrganizationEmailDefaultValues = (
  storedData?: Partial<OrganizationEmailFormValues>,
  { shouldCreateTask = false }: OrganizationEmailDefaultValuesOptions = {}
): OrganizationEmailFormValues => ({
  [OrganizationEmailFormField.Recipient]: getEmailContactsValue(
    storedData?.[OrganizationEmailFormField.Recipient]
  ),
  [OrganizationEmailFormField.Title]:
    storedData?.[OrganizationEmailFormField.Title] ?? '',
  [OrganizationEmailFormField.Message]:
    storedData?.[OrganizationEmailFormField.Message] ?? '',
  [OrganizationEmailFormField.ThreadMembers]: getEmailContactsValue(
    storedData?.[OrganizationEmailFormField.ThreadMembers]
  ),
  [OrganizationEmailFormField.ShouldCreateTask]:
    storedData?.[OrganizationEmailFormField.ShouldCreateTask] ??
    shouldCreateTask,
});

/**
 * Returns all emails selected in a contact field, across all tag types.
 */
export const getEmailContactsList = (value: EmailContactsValue): string[] =>
  Object.values(value).flat();

const removeWhitespace = (value: string) => value.replace(/ /g, '');

/**
 * Builds a `mailto:` link that opens the user's mail client with the email
 * prefilled.
 */
export const buildMailtoLink = ({
  recipient,
  threadMembers,
  emailTitle,
  message,
}: Omit<
  OrganizationEmailFormValues,
  OrganizationEmailFormField.ShouldCreateTask
>): string => {
  const to = removeWhitespace(getEmailContactsList(recipient).join(', '));
  const cc = removeWhitespace(getEmailContactsList(threadMembers).join(', '));
  const params = [
    ['subject', emailTitle],
    ['body', message],
    ['cc', cc],
  ]
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

  return `mailto:${encodeURIComponent(to)}?${params}`;
};

/**
 * Maps email addresses to task tags, keeping only emails that match a
 * selected contact. Duplicate emails produce a single tag.
 */
export const buildEmailTaskTags = (
  emails: string[],
  selectedContacts: ContactOption[]
): CreateTaskTagDto[] =>
  [...new Set(emails)]
    .map(email => selectedContacts.find(contact => contact.email === email))
    .filter((contact): contact is ContactOption => !!contact?.id)
    .map(({ id, type }) => ({ id, type }));
